import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import {
  AdditionalModal,
  EmptyState,
  ExportButton,
  FilterButton,
  LimitPaginationButton,
  Loading,
  PageTitle,
  PaginationGroup,
  SaveFilterBlock,
  TableColumnSetting,
  TableTitle
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import SentimentTable from './parts/SentimentTable/SentimentTable';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';

import {
  exportConfigSentiment,
  mainTableConf,
  defaultFilters,
  defaultNavigation
} from './helpers/configurates';
import { getRequest, postRequest } from 'utils/requests';

import {
  replaceHistory,
  paddingForMenuSettings,
  checkingForExistenceOfOptions,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters
} from 'utils/common';
import { createReqBody } from './helpers/helpers';

import classes from './SentimentBoard.module.scss';

const SentimentBoard = () => {
  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });

  const [filterBackend, setFilterBackend] = useState({
    key: 'sentBoard',
    value: []
  });

  const [visibleFilterSave, setVisibleFilterSave] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [trigerResetDatePicker, setTrigerResetDatePicker] = useState(false);

  const [filterButton, setFilterButton] = useState(false);
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(mainTableConf))
  );

  const [sentimentData, setSentimentData] = useState({});
  const [sentimentFilters, setSentimentFilters] = useState({});

  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(false);
  const [dataConection, setDataConection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  const [startFilter, setStartFilter] = useState(false);

  const [prevStateFilter, setPrevStateFilter] = useState();
  const [prevStateTableConf, setPrevStateTableConf] = useState();

  const [triggerSaveTableSettings, setTriggerSaveTableSettings] =
    useState(false);
  const [triggerStartEffect, setTriggerStartEffect] = useState(false);
  const [enabledRequest, setEnabledRequest] = useState(true);
  const [isOpenAdditional, setIsOpenAdditional] = useState(false);
  const [infoForAdditionalModal, setInfoForAdditionalModal] = useState({});

  const { t } = useTranslation(['activity']);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const tableHeight = useRef();
  const unmount = useRef(false);

  const onOpenAdditionalModal = info => {
    setInfoForAdditionalModal(info);
    setIsOpenAdditional(true);
  };

  const handleFilterInput = e => {
    setFilter({ ...filter, search: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, value =>
    handleFilterInput({ target: { value } })
  );

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const handleLimitChange = limit => {
    if (filter.itemsPerPage === limit) {
      return;
    }
    setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  const handleSortable = field => () => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = e => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  const onClearSearch = () => {
    setFilter({ ...filter, search: '' });
    setStartFilter(!startFilter);
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = listFilter => {
    setSelectLoader(true);
    getRequest('/sentiment/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setSentimentFilters({ ...sentimentFilters, ...body });
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              setEnabledRequest,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  useEffect(() => {
    if (triggerStartEffect) {
      if (
        (!isEqual(prevStateFilter, filter) &&
          JSON.parse(query.get('filter')) !== null) ||
        !isEqual(
          parsConfigForHistory(prevStateTableConf),
          parsConfigForHistory(tableConf)
        )
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          false,
          false,
          changeFilter,
          parsConfigForHistory(tableConf)
        );
      }
    }
    setTriggerStartEffect(true);
    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    if (enabledRequest) {
      setLoaderTable(true);
      if (
        query.get('filter') === null &&
        isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        requestForFilteredData();
      } else if (
        query.get('filter') === null &&
        !isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        resetAllfilters(true);
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      } else {
        setFilter(JSON.parse(query.get('filter')));
        setPrevStateFilter(JSON.parse(query.get('filter')));
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(query.get('tableConf')))
        );
        requestForFilteredData(JSON.parse(query.get('filter')));
        setChangeFilter(query.get('changeFilter'));
        setTableConf(
          reParsConfigForHistory(
            JSON.parse(query.get('tableConf')),
            mainTableConf
          )
        );
      }
      setTrigerResetDatePicker(!trigerResetDatePicker);
    }
    setEnabledRequest(true);

    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/sentiment/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;
    const REQ_BODY = createReqBody(listFilter);
    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setSentimentData(res.body);
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };

  const resetAllfilters = (
    reload,
    onlyFilterLabel,
    onClickDefault,
    modeClearAll
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  if (loader) {
    return <Loading />;
  }

  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      {isOpenAdditional && (
        <AdditionalModal
          isOpen={isOpenAdditional}
          setIsOpen={setIsOpenAdditional}
          info={infoForAdditionalModal}
        />
      )}
      <div className={classes.header}>
        <PageTitle title={t('activity:sentiment.SENTIMENT_BOARD')} />
      </div>

      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.SENTIMENT_BOARD_SEARCH_PLACEHOLDER')}
          value={filter.search}
          onChange={handleFilterInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllfilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={trigerResetDatePicker}
          setTrigerResetDatePicker={setTrigerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
          tableConf={tableConf}
          defaulTableConf={mainTableConf}
          setTableConf={setTableConf}
          triggerSaveTableSettings={triggerSaveTableSettings}
        />
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        sentimentFilters={sentimentFilters}
        filterButton={filterButton}
        trigerResetDatePicker={trigerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      <div className={classes.actions}>
        <TableTitle
          title={t('activity:sentiment.SENTIMENT_BOARD')}
          additional={sentimentData.totalRecords}
        />
        <div className={classes.actions_right}>
          <TableColumnSetting
            setFilterButton={setFilterButton}
            tableConf={tableConf}
            setTableConf={setTableConf}
            setVisibleFilterSave={setVisibleFilterSave}
            setVisibleFilterEdit={setVisibleFilterEdit}
            changeFilter={changeFilter}
            visibleFilterSave={visibleFilterSave}
            visibleFilterEdit={visibleFilterEdit}
            setTriggerSaveTableSettings={setTriggerSaveTableSettings}
            triggerSaveTableSettings={triggerSaveTableSettings}
            defaulTableConf={mainTableConf}
          />
          <ExportButton
            exportConfig={exportConfigSentiment}
            changeOptions={tableConf}
            isDisabled={!sentimentData.totalRecords}
          />
          <LimitPaginationButton
            currentLimit={filter.itemsPerPage}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      <div style={paddingForMenuSettings(tableConf, tableHeight)}>
        <div ref={tableHeight}>
          <SentimentTable
            data={sentimentData.data}
            handleSortable={handleSortable}
            loader={loaderTable}
            tableConf={tableConf}
            filter={filter}
            onOpenAdditionalModal={onOpenAdditionalModal}
            searchText={filter.search}
          />
        </div>
        {sentimentData.data &&
        loaderTable === false &&
        sentimentData.totalPages > 1 ? (
          <PaginationGroup
            pageCount={sentimentData.totalPages}
            setPage={changeCurrentPage}
            currentPage={filter.currentPage}
            itemsTotal={sentimentData.totalRecords}
            itemsPerPage={filter.itemsPerPage}
          />
        ) : null}
      </div>
    </>
  );
};

export default SentimentBoard;
